import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import ContactLogo from './ContactLogo';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Users,
  User,
  Palette,
  Target,
  BarChart3,
  Plug,
  BookOpen,
  Briefcase,
  TrendingUp,
  Settings,
  ShoppingCart,
  ClipboardList,
  Star,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Keyboard,
  Code,
  Send,
  Flag,
} from 'lucide-react';

// Real (lucide-react), uncolored icons for each sidebar section — inherits
// currentColor from .sidebar-item / .sidebar-item.active, same as before.
// Exported so CommandPalette can render the same icon per item without
// duplicating this map.
export const SIDEBAR_ICONS = {
  users: Users,
  user: User,
  palette: Palette,
  target: Target,
  chart: BarChart3,
  plug: Plug,
  book: BookOpen,
  briefcase: Briefcase,
  trending: TrendingUp,
  settings: Settings,
  'shopping-cart': ShoppingCart,
  'clipboard-list': ClipboardList,
  star: Star,
  code: Code,
  send: Send,
  flag: Flag,
};

// Loads the homepage's brand font so .sidebar-group-label (see App.css) can
// use it — declared here via an injected <style> tag rather than in App.css
// directly, since CRA's css-loader can't resolve a public-root url() path
// like '/fonts/...' from an imported .css file (same pattern as Homepage.js).
// Also carries the CSS-only tooltip styling used for data-tooltip attributes
// (collapsed-sidebar labels), replacing the native browser title attribute.
const SIDEBAR_STYLE_CSS = `
@font-face {
  font-family: 'abcNormal';
  src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

[data-tooltip] {
  position: relative;
}
[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%) translateX(-4px);
  background: #1d1d1f;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.01em;
  padding: 5px 9px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.12s ease, transform 0.12s ease;
  transition-delay: 0.05s;
  z-index: 60;
  box-shadow: 0 4px 14px rgba(0,0,0,0.18);
}
[data-tooltip]:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(0);
}

`;

// Maps each sidebar tab id to the plan feature it requires.
// Tabs not listed are free for everyone (Free + Pro).
// Note: "Inicio" stays unlocked — Free users can see their single profile;
// the actual lock fires when they try to add a 2nd one.
// Leads and Analytics are intentionally NOT listed here even though Free
// users don't get the full feature: both now have partial access (their own
// panel decides what to show/blur), so the sidebar shouldn't badge them as
// locked — that treatment is reserved for all-or-nothing tabs.
const TAB_FEATURE_MAP = {
  teams: 'teams',
  team_analytics: 'team_analytics',
  integrations: 'google_sheets',
  hub: 'hub',
  developer: 'developer_api',
  campaigns: 'campaigns',
  followups: 'reminders',
};

// Lock badge label per tab — defaults to 'Pro'; only Enterprise-exclusive
// tabs (like Teams) need to say otherwise.
const TAB_REQUIRED_PLAN_LABEL = {
  teams: 'Enterprise',
  team_analytics: 'Enterprise',
};

// Computes the same nav item list the sidebar renders, given access type,
// user and plan — extracted so CommandPalette can mirror it exactly instead
// of re-deriving its own (and risking drift from the sidebar's rules).
export const getMenuItems = ({ accessType, user, plan, t = (k, es) => es }) => {
  let menuItems = accessType === 'team_key'
    ? [
        // Limited menu for team access users
        { id: 'profile', name: t('sidebar.editProfile', 'Editar Perfil'), icon: 'user' },
        { id: 'leads', name: t('sidebar.leads', 'Leads'), icon: 'target' },
        { id: 'analytics', name: t('sidebar.analytics', 'Analytics'), icon: 'chart' },
        { id: 'appearance', name: t('sidebar.appearance', 'Apariencia'), icon: 'palette' }
      ]
    : [
        // Full menu for normal users, grouped by purpose
        { id: 'profiles', name: t('sidebar.home', 'Inicio'), icon: 'users', group: t('sidebar.groupProfile', 'Perfil') },
        { id: 'profile', name: t('sidebar.editProfile', 'Editar Perfil'), icon: 'user', group: t('sidebar.groupProfile', 'Perfil') },
        { id: 'appearance', name: t('sidebar.appearance', 'Apariencia'), icon: 'palette', group: t('sidebar.groupProfile', 'Perfil') },
        // Free plan: Leads + Analytics are the two partially-unlocked tabs,
        // so they're pinned together at the top of "Crecimiento" — makes it
        // obvious those two are actually usable vs. the fully-locked rest.
        // Pro/Enterprise keep the original order (nothing to highlight there).
        ...(plan && !plan.is_pro
          ? [
              { id: 'leads', name: t('sidebar.leads', 'Leads'), icon: 'target', group: t('sidebar.groupGrowth', 'Crecimiento') },
              { id: 'analytics', name: t('sidebar.analytics', 'Analytics'), icon: 'chart', group: t('sidebar.groupGrowth', 'Crecimiento') },
              { id: 'campaigns', name: t('sidebar.campaigns', 'Eventos'), icon: 'flag', group: t('sidebar.groupGrowth', 'Crecimiento') },
            ]
          : [
              { id: 'leads', name: t('sidebar.leads', 'Leads'), icon: 'target', group: t('sidebar.groupGrowth', 'Crecimiento') },
              { id: 'campaigns', name: t('sidebar.campaigns', 'Eventos'), icon: 'flag', group: t('sidebar.groupGrowth', 'Crecimiento') },
              { id: 'analytics', name: t('sidebar.analytics', 'Analytics'), icon: 'chart', group: t('sidebar.groupGrowth', 'Crecimiento') },
            ]),
        { id: 'integrations', name: t('sidebar.integrations', 'Integraciones'), icon: 'plug', group: t('sidebar.groupGrowth', 'Crecimiento') },
        { id: 'hub', name: t('sidebar.hub', 'Hub'), icon: 'book', group: t('sidebar.groupGrowth', 'Crecimiento') },
        { id: 'followups', name: t('sidebar.followups', 'Follow-ups'), icon: 'send', group: t('sidebar.groupGrowth', 'Crecimiento') },
        { id: 'developer', name: t('sidebar.developer', 'Desarrollador'), icon: 'code', group: t('sidebar.groupGrowth', 'Crecimiento') },
        { id: 'teams', name: t('sidebar.teams', 'Teams'), icon: 'briefcase', group: t('sidebar.groupTeam', 'Equipo') },
        { id: 'team_analytics', name: t('sidebar.teamAnalytics', 'Analítica de Equipo'), icon: 'trending', group: t('sidebar.groupTeam', 'Equipo') },
      ];

  // Add admin menu if user is admin (only for normal login)
  if (accessType === 'normal' && user.is_admin) {
    menuItems.push({ id: 'admin', name: t('sidebar.admin', 'Administración'), icon: 'settings', group: t('sidebar.groupAdmin', 'Administración') });
    menuItems.push({ id: 'product', name: t('sidebar.productManagement', 'Gestión de Producto'), icon: 'shopping-cart', group: t('sidebar.groupAdmin', 'Administración') });
    menuItems.push({ id: 'orders', name: t('sidebar.orders', 'Pedidos'), icon: 'clipboard-list', group: t('sidebar.groupAdmin', 'Administración') });
    menuItems.push({ id: 'subscriptions', name: t('sidebar.subscriptions', 'Suscripciones'), icon: 'star', group: t('sidebar.groupAdmin', 'Administración') });
  }

  return menuItems;
};

const Sidebar = ({
  activeTab,
  setActiveTab,
  onLogout,
  user,
  profiles,
  currentProfile,
  setCurrentProfile,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  isSidebarCollapsed = false,
  setIsSidebarCollapsed = () => {},
  onOpenCommandPalette = () => {},
  onOpenShortcuts = () => {},
  accessType = 'normal',
  teamName = ''
}) => {
  const { hasFeature, plan, openUpgrade } = usePlan();
  const { t } = useLanguage();
  // Define menu items based on access type, grouped for visual hierarchy
  const menuItems = getMenuItems({ accessType, user, plan, t });

  // Compute lock state per menu item (Pro lock badge). Skip for admin tabs.
  const ADMIN_TABS = new Set(['admin', 'product', 'orders', 'subscriptions']);
  const isTabLocked = (tabId) => {
    if (ADMIN_TABS.has(tabId)) return false;
    if (accessType === 'team_key') return false; // team access bypass
    const required = TAB_FEATURE_MAP[tabId];
    if (!required) return false;
    return !hasFeature(required);
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // Close mobile sidebar when tab is selected
    if (setIsMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  };

  // Group items in display order (object insertion order == first-seen order)
  const groupedItems = menuItems.reduce((acc, item) => {
    const g = item.group || '_none';
    (acc[g] = acc[g] || []).push(item);
    return acc;
  }, {});

  const displayName = accessType === 'team_key' && currentProfile ? currentProfile.name : user.name;

  const renderItem = (item) => {
    const locked = isTabLocked(item.id);
    const IconComp = SIDEBAR_ICONS[item.icon];
    return (
      <button
        key={item.id}
        data-testid={`sidebar-tab-${item.id}`}
        onClick={() => handleTabClick(item.id)}
        data-tutorial={
          item.id === 'profiles' ? 'my-profiles' :
          item.id === 'profile' ? 'edit-profile' :
          item.id === 'analytics' ? 'analytics' :
          item.id === 'appearance' ? 'appearance' :
          undefined
        }
        data-tooltip={isSidebarCollapsed ? item.name : undefined}
        className={`sidebar-item w-full text-left ${
          activeTab === item.id ? 'active' : ''
        } ${locked ? 'sidebar-item-locked' : ''} ${isSidebarCollapsed ? 'lg:justify-center' : ''}`}
      >
        {IconComp && <IconComp className="w-4 h-4" strokeWidth={1.75} />}
        <span className={`flex-1 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
        {item.badge && !locked && (
          <span className={`hub-badge-new ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>
            {item.badge}
          </span>
        )}
        {locked && (
          <span
            data-testid={`sidebar-lock-${item.id}`}
            className={`sidebar-pro-badge ${isSidebarCollapsed ? 'lg:hidden' : ''}`}
            title={t('sidebar.requiresPlan', `Requiere plan ${TAB_REQUIRED_PLAN_LABEL[item.id] || 'Pro'}`).replace('{plan}', TAB_REQUIRED_PLAN_LABEL[item.id] || 'Pro')}
          >
            {TAB_REQUIRED_PLAN_LABEL[item.id] || 'Pro'}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={`
      ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      lg:translate-x-0
      fixed lg:sticky
      lg:top-0
      lg:h-screen
      inset-y-0 left-0
      z-30
      w-64 ${isSidebarCollapsed ? 'lg:w-16' : 'lg:w-56'}
      modern-sidebar
      transition-transform duration-300 ease-in-out
      lg:transition-none
      flex flex-col
    `}>
      <style>{SIDEBAR_STYLE_CSS}</style>
      {/* Mobile Close Button */}
      <div className="lg:hidden absolute top-4 right-4">
        <button
          onClick={() => setIsMobileSidebarOpen(false)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Logo + collapse toggle */}
      <div className={`flex items-center ${isSidebarCollapsed ? 'lg:justify-center' : 'justify-between'}`} style={{padding:'16px 12px 12px'}}>
        <div className={isSidebarCollapsed ? 'lg:hidden' : ''}>
          <ContactLogo className="h-7 w-auto" clickable={true} />
        </div>
        <div style={{display:'flex',alignItems:'center',gap:4}}>
          <button
            onClick={onOpenCommandPalette}
            className="flex items-center justify-center"
            style={{width:26,height:26,borderRadius:6,color:'#A3A3A3',flexShrink:0,transition:'background 0.15s, color 0.15s'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#F5F5F5';e.currentTarget.style.color='#1d1d1f'}}
            onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#A3A3A3'}}
            data-tooltip={t('sidebar.search', 'Buscar (⌘K)')}
          >
            <Search className="w-4 h-4" strokeWidth={1.75} />
          </button>
          {!isSidebarCollapsed && (
            <button
              onClick={onOpenShortcuts}
              className="flex items-center justify-center"
              style={{width:26,height:26,borderRadius:6,color:'#A3A3A3',flexShrink:0,transition:'background 0.15s, color 0.15s'}}
              onMouseEnter={e=>{e.currentTarget.style.background='#F5F5F5';e.currentTarget.style.color='#1d1d1f'}}
              onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#A3A3A3'}}
              data-tooltip={t('sidebar.shortcuts', 'Atajos de teclado (?)')}
            >
              <Keyboard className="w-4 h-4" strokeWidth={1.75} />
            </button>
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="hidden lg:flex items-center justify-center"
            style={{width:26,height:26,borderRadius:6,color:'#A3A3A3',flexShrink:0,transition:'background 0.15s, color 0.15s'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#F5F5F5';e.currentTarget.style.color='#1d1d1f'}}
            onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#A3A3A3'}}
            data-tooltip={isSidebarCollapsed ? t('sidebar.expandMenu', 'Expandir menú') : t('sidebar.collapseMenu', 'Colapsar menú')}
          >
            {isSidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" strokeWidth={1.75} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {/* User / Plan Card */}
      <div className={`sidebar-user-card ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>
        <p className="sidebar-user-name">{displayName}</p>
        {accessType === 'team_key' ? (
          <span className="sidebar-plan-badge sidebar-plan-badge--team">
            {teamName || t('sidebar.teamAccess', 'Acceso Teams')}
          </span>
        ) : user.is_admin ? (
          <span className="sidebar-plan-badge sidebar-plan-badge--admin">
            Admin
          </span>
        ) : (
          <>
            <span className={`sidebar-plan-badge sidebar-plan-badge--${plan.plan_id === 'enterprise' ? 'enterprise' : plan.plan_id === 'pro' ? 'pro' : 'free'}`}>
              {plan.plan_id === 'free' ? t('sidebar.freePlan', 'Plan Gratuito') : `${t('sidebar.planPrefix', 'Plan')} ${plan.plan_name}`}
            </span>
            {plan.plan_id !== 'enterprise' && (
              <button
                onClick={() => openUpgrade(null)}
                className="sidebar-compare-plans-link"
              >
                {t('sidebar.comparePlans', 'Comparar planes')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="mt-1 flex-1 overflow-y-auto px-3">
        {Object.entries(groupedItems).map(([group, items], gi) => (
          <div key={group} className={gi > 0 ? 'sidebar-group' : 'sidebar-group sidebar-group--first'}>
            {group !== '_none' && <div className={`sidebar-group-label ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>{group}</div>}
            {items.map(renderItem)}
          </div>
        ))}
      </nav>

      {/* Footer Actions */}
      <div style={{padding:'12px 10px',borderTop:'1px solid #E5E5E5',display:'flex',flexDirection:'column',gap:4}}>
        {accessType === 'normal' && (
          <button
            onClick={() => handleTabClick('settings')}
            data-tooltip={isSidebarCollapsed ? t('sidebar.settings', 'Configuración') : undefined}
            className={`sidebar-item ${activeTab === 'settings' ? 'active' : ''} ${isSidebarCollapsed ? 'lg:justify-center' : ''}`}
          >
            <Settings className="w-4 h-4" strokeWidth={1.75} />
            <span className={`flex-1 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>{t('sidebar.settings', 'Configuración')}</span>
          </button>
        )}
        {currentProfile && (
          <button
            onClick={() => {
              window.open(`/pages/${encodeURIComponent(currentProfile.slug)}`, '_blank');
              if (setIsMobileSidebarOpen) setIsMobileSidebarOpen(false);
            }}
            className={isSidebarCollapsed ? 'lg:hidden' : ''}
            style={{width:'100%',background:'#fff',color:'#0A0A0A',border:'1px solid #E5E5E5',borderRadius:8,padding:'9px 12px',fontSize:13.5,fontWeight:500,cursor:'pointer',textAlign:'left',letterSpacing:'-0.01em',transition:'background 0.15s,border-color 0.15s'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#FAFAFA';e.currentTarget.style.borderColor='#D4D4D4'}}
            onMouseLeave={e=>{e.currentTarget.style.background='#fff';e.currentTarget.style.borderColor='#E5E5E5'}}
          >
            {t('sidebar.viewCurrentProfile', 'Ver perfil actual')}
          </button>
        )}
        <button
          onClick={() => {
            onLogout();
            if (setIsMobileSidebarOpen) setIsMobileSidebarOpen(false);
          }}
          className={isSidebarCollapsed ? 'lg:hidden' : ''}
          style={{width:'100%',background:'none',border:'none',borderRadius:8,padding:'9px 12px',fontSize:13.5,fontWeight:500,color:'#6B6B6B',cursor:'pointer',textAlign:'left',letterSpacing:'-0.01em',transition:'background 0.12s, color 0.12s'}}
          onMouseEnter={e=>{e.currentTarget.style.background='#F5F5F5';e.currentTarget.style.color='#0A0A0A'}}
          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#6B6B6B'}}
        >
          {t('sidebar.logout', 'Cerrar sesión')}
        </button>
      </div>
    </div>
  );
};

// Tutorial Component with Joyride

export default Sidebar;
